import { MenuItem } from "@mui/material";
import React from "react";
import {
  type PictureZenbujikoOptionKey,
  type AcquireMultipleBookHandoffData,
  type BookTypeEng,
  type BookTypeJpn,
  type PictureTypeData,
  type PictureTypeKey,
  type AcquireMultipleBookLocationState,
} from "@/types/acquirebook";
import { type ISelectObject } from "@/types/select";
import { type CityListResponse } from "@/types/city";
import { type PropertySelectionRow } from "@/features/map/types"; // TODO: 場所を移動したいが、参照している場所が多いので、後回しにする
import {
  convertCirclesToZero,
  NYHalfWidthNumberToFillWidthNumber,
} from "./utils";

// 都道府県リスト
const prefectures: Array<{ id: number; val: string }> = [
  { id: 1, val: "北海道" },
  { id: 2, val: "青森県" },
  { id: 3, val: "岩手県" },
  { id: 4, val: "宮城県" },
  { id: 5, val: "秋田県" },
  { id: 6, val: "山形県" },
  { id: 7, val: "福島県" },
  { id: 8, val: "茨城県" },
  { id: 9, val: "栃木県" },
  { id: 10, val: "群馬県" },
  { id: 11, val: "埼玉県" },
  { id: 12, val: "千葉県" },
  { id: 13, val: "東京都" },
  { id: 14, val: "神奈川県" },
  { id: 15, val: "新潟県" },
  { id: 16, val: "富山県" },
  { id: 17, val: "石川県" },
  { id: 18, val: "福井県" },
  { id: 19, val: "山梨県" },
  { id: 20, val: "長野県" },
  { id: 21, val: "岐阜県" },
  { id: 22, val: "静岡県" },
  { id: 23, val: "愛知県" },
  { id: 24, val: "三重県" },
  { id: 25, val: "滋賀県" },
  { id: 26, val: "京都府" },
  { id: 27, val: "大阪府" },
  { id: 28, val: "兵庫県" },
  { id: 29, val: "奈良県" },
  { id: 30, val: "和歌山県" },
  { id: 31, val: "鳥取県" },
  { id: 32, val: "島根県" },
  { id: 33, val: "岡山県" },
  { id: 34, val: "広島県" },
  { id: 35, val: "山口県" },
  { id: 36, val: "徳島県" },
  { id: 37, val: "香川県" },
  { id: 38, val: "愛媛県" },
  { id: 39, val: "高知県" },
  { id: 40, val: "福岡県" },
  { id: 41, val: "佐賀県" },
  { id: 42, val: "長崎県" },
  { id: 43, val: "熊本県" },
  { id: 44, val: "大分県" },
  { id: 45, val: "宮崎県" },
  { id: 46, val: "鹿児島県" },
  { id: 47, val: "沖縄県" },
];

const getPrefectureIdByValue = (value: string): number | undefined => {
  return prefectures.find((pref) => pref.val === value)?.id;
};

// IDから県名を返す、失敗した場合はundefined
const getPrefectureNameById = (id: number): string | undefined => {
  return prefectures.find((pref) => pref.id === id)?.val;
};

// 不動産種別の文字列の英語版/日本語版を変換するテーブル
const bookTypeTableToJpn: Record<BookTypeEng, BookTypeJpn> = {
  LAND: "土地" as BookTypeJpn,
  BUILDING: "建物" as BookTypeJpn,
  CONDOMINIUM: "区分建物" as BookTypeJpn,
  ONE_BUILDING: "一棟" as BookTypeJpn,
};

const convertBookTypeToJpn = (type: BookTypeEng): BookTypeJpn => {
  return bookTypeTableToJpn[type];
};
const convertBookTypeToEng = (type: BookTypeJpn): BookTypeEng => {
  for (const prop in bookTypeTableToJpn) {
    if (bookTypeTableToJpn[prop as BookTypeEng] === type)
      return prop as BookTypeEng;
  }
  // 引数が正しければ、ここに来ることはないけど、書かないとエラーになるので書く
  return "LAND" as BookTypeEng;
};

// 不動産種別の選択値と、その時のチェックボックスの有効/無効の状態を示すテーブル
// falseのときは"disabled = false"なので有効となる
const OptionsEnableDisableTable: Record<
  string,
  Record<PictureTypeKey | PictureZenbujikoOptionKey, boolean>
> = {
  LAND: {
    SHOYUSHAJIKO: false,
    ZENBUJIKO: false,
    SHINTAKUMOKUROKU: false,
    KYODOTANPOMOKUROKU: false,
    GENZAIJIKO: false,
    CHISEKISOKURYOZU: false,
    DENSHIKOZU: false,
    CHIEKIKENZUMEN: false,
    TATEMONOZUMEN: true,
  },
  BUILDING: {
    SHOYUSHAJIKO: false,
    ZENBUJIKO: false,
    SHINTAKUMOKUROKU: false,
    KYODOTANPOMOKUROKU: false,
    GENZAIJIKO: false,
    CHISEKISOKURYOZU: true,
    DENSHIKOZU: true,
    CHIEKIKENZUMEN: true,
    TATEMONOZUMEN: false,
  },
  CONDOMINIUM: {
    SHOYUSHAJIKO: false,
    ZENBUJIKO: false,
    SHINTAKUMOKUROKU: false,
    KYODOTANPOMOKUROKU: false,
    GENZAIJIKO: false,
    CHISEKISOKURYOZU: true,
    DENSHIKOZU: true,
    CHIEKIKENZUMEN: true,
    TATEMONOZUMEN: false,
  },
};

// 都道府県セレクトボックスで表示するメニューを生成する
const getPrefectureMenuItems = (): React.ReactNode => {
  return prefectures.map((pref) => (
    <MenuItem key={pref.id} value={pref.id}>
      {pref.val}
    </MenuItem>
  ));
};

// 都道府県セレクトボックスで表示するメニューを生成する（react hook form）
const getPrefectureSelectObject = (): ISelectObject[] => {
  return prefectures.map((pref) => ({
    value: String(pref.id),
    label: pref.val,
  }));
};

// 市区町村のセレクトボックスの選択項目を生成する（react hook form）
const getCitySelectObject = (
  cityListResponse: CityListResponse
): ISelectObject[] => {
  const cityList = cityListResponse.list;
  if (cityList.length > 0) {
    return cityList.map((value) => ({
      value: value.cityCode,
      label: value.name,
    }));
  }
  return [];
};

const getRealEstateTypeEngSelectObject = (): ISelectObject[] => {
  return [
    { value: "LAND", label: "土地" },
    { value: "BUILDING", label: "建物" },
    { value: "CONDOMINIUM", label: "区分建物" },
  ];
};

/**
 * 「地番または家屋番号」の変換処理
 * @param value
 * @returns 変換後の文字列
 */
const transformChibanKaokuNumber = (value: string): string => {
  // 本来であればハイフン以外の記号は存在しないが、記号の変換処理は未実装となっている
  const transformedValue = String(value)
    .trim()
    .replace(/(番地)$|[番号]$/g, "")
    .replace(/(番地の)|(番地)|(番の)|[番の]/g, "-")
    .replace(/[\s\u3000]/g, ""); // 全角半角スペース削除
  return NYHalfWidthNumberToFillWidthNumber(
    convertCirclesToZero(transformedValue)
  );
};

const getInitialPictureRequestOptions = ({
  realEstateTypeEng,
  hasReuseData,
  oldPictureCheckOptions,
  preserveCheckedState = false,
}: {
  realEstateTypeEng: string;
  hasReuseData: boolean;
  oldPictureCheckOptions?: PictureTypeData;
  preserveCheckedState?: boolean;
}): PictureTypeData => {
  // 図面種別を選択するチェックボックスの初期値
  // HACK: 関数外で定義すると、再代入は不可能だが、オブジェクトの値は変えることができるので関数内に定義する
  const RequestOptionsInitialValue: PictureTypeData = {
    SHOYUSHAJIKO: {
      checked: false,
      disabled: true,
      reuse: null,
    },
    ZENBUJIKO: {
      checked: false,
      disabled: true,
      reuse: null,
      option: {
        SHINTAKUMOKUROKU: { checked: false, disabled: true },
        KYODOTANPOMOKUROKU: { checked: false, disabled: true },
        GENZAIJIKO: { checked: false, disabled: true },
      },
    },
    CHISEKISOKURYOZU: {
      checked: false,
      disabled: true,
      reuse: null,
    },
    DENSHIKOZU: {
      checked: false,
      disabled: true,
      reuse: null,
    },
    CHIEKIKENZUMEN: {
      checked: false,
      disabled: true,
      reuse: null,
    },
    TATEMONOZUMEN: {
      checked: false,
      disabled: true,
      reuse: null,
    },
  };

  // 空文字列の場合は全て無効化する
  if (realEstateTypeEng === "") {
    return RequestOptionsInitialValue;
  }
  // realEstateTypeの値がOptionsEnableDisableTableにあるkey["土地", "建物", "区分建物"]に当てはまるときだけ実行を継続する
  // TODO realEstateTypeEng: Exclude<BookTypeEng, "ONE_BUILDING"> | ""にしたいが、影響範囲が大きいためstringにしているため、ここでチェックを行う
  const isReadEstateTypeValid: boolean = Object.keys(
    OptionsEnableDisableTable
  ).includes(realEstateTypeEng);
  if (!isReadEstateTypeValid) return RequestOptionsInitialValue;

  // チェックボックスをOptionsEnableDisableTableに示されている状態に合わせる
  const newPictureCheckOptions = oldPictureCheckOptions
    ? { ...oldPictureCheckOptions }
    : { ...RequestOptionsInitialValue }; // ユーザ操作がない場合は初期値

  for (const val in newPictureCheckOptions) {
    const key = val as PictureTypeKey;
    if (OptionsEnableDisableTable[realEstateTypeEng][key]) {
      // 指示値がtrueならチェックボックス無効、チェック解除、再利用情報をnullにする
      newPictureCheckOptions[key].disabled = true;
      newPictureCheckOptions[key].checked = false;
      newPictureCheckOptions[key].reuse = null;
    } else {
      // 指示値falseなら表示する
      newPictureCheckOptions[key].disabled = false;
      // 保持する状態がfalseならチェックを外す
      if (!preserveCheckedState) newPictureCheckOptions[key].checked = false;
      if (hasReuseData && newPictureCheckOptions[key].checked) {
        // 再利用情報がある場合かつ、チェックが入っている場合は再利用情報を初期値に設定
        newPictureCheckOptions[key].reuse = "REUSE_NEW";
      } else {
        // 再利用情報がない場合はnullに設定
        newPictureCheckOptions[key].reuse = null;
      }
    }

    if (key === "ZENBUJIKO") {
      // 上記と同様に全部事項関連のチェックボックスの状態を更新
      for (const zenbujikoVal in newPictureCheckOptions[key].option) {
        const zenbujikoKey = zenbujikoVal as PictureZenbujikoOptionKey;
        if (OptionsEnableDisableTable[realEstateTypeEng][zenbujikoKey]) {
          // 指示値がtrueならチェックボックス無効とチェック解除を行う
          newPictureCheckOptions[key].option[zenbujikoKey].disabled = true;
          newPictureCheckOptions[key].option[zenbujikoKey].checked = false;
        } else {
          // 指示値falseなら表示する
          newPictureCheckOptions[key].option[zenbujikoKey].disabled = false;
          // 保持する状態がfalseならチェックを外す
          if (!preserveCheckedState)
            newPictureCheckOptions[key].option[zenbujikoKey].checked = false;
        }
      }
    }
  }
  return newPictureCheckOptions;
};

/**
 * 家屋番号検索ボタンの活性非活性制御。
 * 「不動産種別」「都道府県」「所在地」(「市区町村」+「大字・町丁目」)「地番または家屋番号」のうち1つでも未入力なら非活性。
 * 「不動産種別」で「土地」以外が選択されていたら非活性。
 * @returns {boolean}
 */
const checkKaokuNumberSearchDisabled = ({
  realEstateTypeEng,
  prefectureId,
  location,
  chibanKaokuNumbers,
}: {
  realEstateTypeEng: string;
  prefectureId: string;
  location: string;
  chibanKaokuNumbers: string[];
}): boolean => {
  if (
    !realEstateTypeEng ||
    !prefectureId ||
    !location ||
    chibanKaokuNumbers.length === 0
  )
    return true;

  if (realEstateTypeEng !== "LAND") return true;

  return false;
};

/**
 * 物件リストに物件を追加する処理のための共通関数
 * @param realEstateTypeEng 不動産種別
 * @param chibanKaokuNumbers 地番または家屋番号の配列
 * @param prefectureId 都道府県ID
 * @param prefectureName 都道府県名
 * @param location 所在地
 * @param rows 追加する物件行データ
 * @returns 一括取得画面に渡すデータ
 */
const createAcquireMultipleBookHandoffData = (
  realEstateTypeEng: BookTypeEng,
  chibanKaokuNumbers: string[],
  prefectureId: string,
  prefectureName: string,
  location: string,
  rows: PropertySelectionRow[] = []
): AcquireMultipleBookHandoffData[] => {
  const states: AcquireMultipleBookHandoffData[] = [...rows].map((row) => {
    return {
      id: row.id,
      bookType: row.bookType,
      prefCode: String(prefectureId),
      prefName: row.prefName,
      locationFull: row.location,
      chibanKaokuNumber: row.chibanKaokuNumber,
      kyoutan: false,
      sintaku: false,
      genzai: false,
      ownerInfo: false,
      electricDrawings: false,
      tisekiDrawings: false,
      tiekiDrawings: false,
      buildingDrawings: false,
    };
  });

  chibanKaokuNumbers.forEach((chibanKaokuNumber) => {
    // 重複しないIDを設定
    const uniqueID = Math.max(...states.map((state) => state.id), 0) + 1;
    states.unshift({
      id: uniqueID,
      bookType: realEstateTypeEng,
      prefCode: String(prefectureId),
      prefName: prefectureName,
      locationFull: location,
      chibanKaokuNumber,
      kyoutan: false,
      sintaku: false,
      genzai: false,
      ownerInfo: false,
      electricDrawings: false,
      tisekiDrawings: false,
      tiekiDrawings: false,
      buildingDrawings: false,
    });
  });

  return states;
};

const pastDaysDescription = (
  <>
    取得済の場合でも経過日数を過ぎたものは再取得いたします
    <br />
    <br />
    経過日数を指定した場合でも、
    <br />
    &quot;取得済登記&quot;、&quot;リユース登記&quot;のラジオボタンのチェックが優先されます
  </>
);

// AcquireMultipleBookLocationState用の型ガードの関数を定義
const isObjectWithAcquireMultipleBookLocationState = (
  obj: unknown
): obj is AcquireMultipleBookLocationState => {
  return (
    obj !== null &&
    typeof obj === "object" &&
    "data" in obj &&
    "identifyName" in obj &&
    "pastDays" in obj
  );
};

// AcquireMultipleBookHandoffData[]用の型ガードの関数を定義
const isArrayOfAcquireMultipleBookHandoffData = (
  obj: unknown
): obj is AcquireMultipleBookHandoffData[] => {
  return Array.isArray(obj);
};

export {
  prefectures,
  getPrefectureMenuItems,
  getPrefectureSelectObject,
  getPrefectureIdByValue,
  getPrefectureNameById,
  getCitySelectObject,
  getRealEstateTypeEngSelectObject,
  getInitialPictureRequestOptions,
  convertBookTypeToJpn,
  convertBookTypeToEng,
  transformChibanKaokuNumber,
  createAcquireMultipleBookHandoffData,
  checkKaokuNumberSearchDisabled,
  pastDaysDescription,
  isObjectWithAcquireMultipleBookLocationState,
  isArrayOfAcquireMultipleBookHandoffData,
};
